import { api } from './services/api';
import { VAPID_PUBLIC_KEY } from './config';

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) %4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = atob(base64)
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function pushSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window && "Notification" in window;
}

/**
 * Was this subscription created with the VAPID key we're using NOW?
 *
 * A subscription is permanently bound to the applicationServerKey it was born
 * with. If the key changes, the push service rejects every send with 403 — and
 * nothing recovers on its own: the browser keeps handing back the same stale
 * subscription, and the server only prunes on 404/410, so the dead row lingers.
 * Silent, permanent, invisible from the UI. Hence this check.
 */
function usesCurrentKey(sub) {
    const applied = sub?.options?.applicationServerKey;
    if (!applied) return true;   // browser didn't expose it — don't churn on a guess
    const current = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
    const existing = new Uint8Array(applied);
    if (existing.length !== current.length) return false;
    return existing.every((byte, i) => byte === current[i]);
}

/**
 * Mint a subscription against the CURRENT key, discarding `stale` first if the
 * caller has one. Takes the existing subscription as an argument rather than
 * re-fetching it — the caller has already looked it up.
 *
 * Safe without a user gesture *once permission is granted* — subscribe() only
 * requires a gesture when it would trigger a permission prompt.
 */
async function subscribeFresh(reg, stale) {
    // Must unsubscribe first: subscribe() with a DIFFERENT applicationServerKey
    // while a subscription exists throws InvalidStateError.
    if (stale) await stale.unsubscribe();
    return reg.pushManager.subscribe({
        userVisibleOnly: true,                     // required on iOS
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
}

export async function enablePushNotifications() {
    const permission = await Notification.requestPermission(); // Must be from a user tap
    if (permission !== 'granted') throw new Error("permission denied.");

    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();

    // No subscription yet, or one bound to a key we no longer sign with.
    if (!sub || !usesCurrentKey(sub)) {
        if (sub) console.warn('push: subscription was made with an old VAPID key — re-subscribing.');
        sub = await subscribeFresh(reg, sub);
    }

    await api.post('/push/subscribe/', sub.toJSON()); // { endpoint, keys: {256dh,auth} }
    return sub;
}

export async function getPushStatus() {
    if (!pushSupported()) return 'unsupported';
    if (Notification.permission === 'denied') return 'blocked';

    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();

    if (Notification.permission === 'granted' && sub && !usesCurrentKey(sub)) {
        // Heal a key rotation on page load, with no user interaction. This is the
        // path that matters in production: if the VAPID pair is ever rotated,
        // every family member's phone fixes itself the next time they open the
        // app, instead of going quietly dead with no way to notice.
        try {
            console.warn('push: VAPID key changed — re-subscribing this device.');
            sub = await subscribeFresh(reg, sub);
        } catch (err) {
            console.warn('push: re-subscribe after key change failed', err);
            return 'off';
        }
    }

    if (Notification.permission === 'granted' && sub) {
      // Self-heal: make sure the server still has THIS device's subscription.
      // (Idempotent — update_or_create keyed on endpoint.)
      try { await api.post('/push/subscribe/', sub.toJSON()); } catch (e) {}
      return 'on';
    }
    return 'off';
  }
